// Finance Books — Reports, GST, Journals, Settings.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import {
  PageHeader, Btn, Drawer, Input, Select, Table, Kpi,
  fmtMoney, fmtDate, todayISO, PANEL, SOFT,
} from '../ui';

// ────────────────────────────────────────────────────────────────────────────
// Financial Reports (P&L, Balance Sheet, Trial Balance, Aging)
// ────────────────────────────────────────────────────────────────────────────
export function FinancialReports() {
  const [tab, setTab] = useState('pl');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState(todayISO());
  const [pl, setPl] = useState(null);
  const [bs, setBs] = useState(null);
  const [tb, setTb] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [apAging, setApAging] = useState(null);

  const refresh = async () => {
    try {
      const params = (start || end) ? `?${start ? `start_date=${start}` : ''}${start && end ? '&' : ''}${end ? `end_date=${end}` : ''}` : '';
      if (tab === 'pl') setPl(await fb.get(`/profit-loss${params}`));
      else if (tab === 'bs') setBs(await fb.get(`/balance-sheet${end ? `?end_date=${end}` : ''}`));
      else if (tab === 'tb') setTb(await fb.get(`/trial-balance${end ? `?end_date=${end}` : ''}`));
      else if (tab === 'ar') setArAging(await fb.get('/aging-receivables'));
      else if (tab === 'ap') setApAging(await fb.get('/aging-payables'));
    } catch (e) { toast.error('Report load failed'); }
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [tab]);

  return (
    <div data-testid="fb-reports">
      <PageHeader title="Financial Reports" subtitle="Live ledger-based · always in sync with transactions" />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        {[
          { k: 'pl', l: 'Profit & Loss' },
          { k: 'bs', l: 'Balance Sheet' },
          { k: 'tb', l: 'Trial Balance' },
          { k: 'ar', l: 'Receivables Aging' },
          { k: 'ap', l: 'Payables Aging' },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${tab === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
        <div className="ml-auto flex gap-2 items-end">
          {(tab === 'pl' || tab === 'bs' || tab === 'tb') && tab === 'pl' && (
            <Input label="From" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          )}
          {(tab !== 'ar' && tab !== 'ap') && (
            <Input label="As of" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          )}
          <Btn onClick={refresh} variant="ghost">Refresh</Btn>
        </div>
      </div>

      {tab === 'pl' && pl && (
        <div className={`${PANEL} rounded-xl p-6`} data-testid="report-pl">
          <h3 className="text-white font-medium mb-3">Profit & Loss · {fmtDate(pl.from)} to {fmtDate(pl.to)}</h3>
          <Section title="Income" rows={pl.income} total={pl.total_income} tone="green" />
          <Section title="Expenses" rows={pl.expenses} total={pl.total_expense} tone="red" />
          <div className="mt-4 border-t border-white/20 pt-3 flex justify-between text-lg font-semibold">
            <span className="text-white">Net Profit</span>
            <span className={`tabular-nums ${pl.net_profit >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmtMoney(pl.net_profit)}</span>
          </div>
        </div>
      )}

      {tab === 'bs' && bs && (
        <div className={`${PANEL} rounded-xl p-6`} data-testid="report-bs">
          <h3 className="text-white font-medium mb-3">Balance Sheet · As of {fmtDate(bs.as_of)}</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Section title="Assets" rows={bs.assets} total={bs.total_assets} tone="green" />
            </div>
            <div>
              <Section title="Liabilities" rows={bs.liabilities} total={bs.total_liabilities} tone="red" />
              <Section title="Equity" rows={bs.equity} total={bs.total_equity} tone="blue" />
              <div className="mt-3 border-t border-white/20 pt-3 flex justify-between font-semibold text-white">
                <span>Liabilities + Equity</span>
                <span className="tabular-nums">{fmtMoney(bs.liab_plus_equity)}</span>
              </div>
            </div>
          </div>
          <div className={`mt-4 text-xs ${bs.balanced ? 'text-emerald-300' : 'text-rose-300'}`}>
            {bs.balanced ? '✓ Balance sheet is balanced' : `✗ Out of balance by ${fmtMoney(Math.abs(bs.total_assets - bs.liab_plus_equity))}`}
          </div>
        </div>
      )}

      {tab === 'tb' && tb && (
        <div data-testid="report-tb">
          <h3 className="text-white font-medium mb-3">Trial Balance · As of {fmtDate(tb.as_of)}</h3>
          <Table
            columns={[
              { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs">{r.code}</span> },
              { key: 'name', label: 'Account' },
              { key: 'type', label: 'Type' },
              { key: 'debit', label: 'Debit', align: 'right', render: (r) => r.debit ? <span className="tabular-nums">{fmtMoney(r.debit)}</span> : '' },
              { key: 'credit', label: 'Credit', align: 'right', render: (r) => r.credit ? <span className="tabular-nums">{fmtMoney(r.credit)}</span> : '' },
            ]}
            rows={tb.rows}
          />
          <div className="mt-3 flex justify-end gap-8 text-sm">
            <span className="text-white/55">Totals:</span>
            <span className="tabular-nums">Dr {fmtMoney(tb.total_debit)}</span>
            <span className="tabular-nums">Cr {fmtMoney(tb.total_credit)}</span>
            <span className={tb.balanced ? 'text-emerald-300' : 'text-rose-300'}>{tb.balanced ? '✓ Balanced' : '✗ Off'}</span>
          </div>
        </div>
      )}

      {tab === 'ar' && arAging && <AgingView data={arAging} who="customers" partyKey="customer_name" testid="report-ar-aging" />}
      {tab === 'ap' && apAging && <AgingView data={apAging} who="vendors" partyKey="vendor_name" testid="report-ap-aging" />}
    </div>
  );
}

function Section({ title, rows, total, tone }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">{title}</div>
      {(!rows || rows.length === 0) ? <p className="text-white/30 italic text-sm">No entries</p> : rows.map((r) => (
        <div key={r.account_id || r.code} className="flex justify-between py-1 text-sm">
          <span className="text-white/75">{r.code} · {r.name}</span>
          <span className="tabular-nums text-white">{fmtMoney(r.amount)}</span>
        </div>
      ))}
      <div className={`mt-2 pt-2 border-t border-white/10 flex justify-between text-sm font-medium ${tone === 'green' ? 'text-emerald-300' : tone === 'red' ? 'text-rose-300' : tone === 'blue' ? 'text-sky-300' : 'text-white'}`}>
        <span>Total {title}</span>
        <span className="tabular-nums">{fmtMoney(total)}</span>
      </div>
    </div>
  );
}

function AgingView({ data, who, partyKey, testid }) {
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-6 gap-3 mb-4">
        <Kpi label="Total" value={fmtMoney(data.total)} tone="orange" />
        <Kpi label="Current" value={fmtMoney(data.buckets.current)} />
        <Kpi label="1-30 days" value={fmtMoney(data.buckets['1-30'])} />
        <Kpi label="31-60 days" value={fmtMoney(data.buckets['31-60'])} />
        <Kpi label="61-90 days" value={fmtMoney(data.buckets['61-90'])} tone="orange" />
        <Kpi label="90+ days" value={fmtMoney(data.buckets['90+'])} tone="red" />
      </div>
      <Table
        columns={[
          { key: partyKey, label: who === 'customers' ? 'Customer' : 'Vendor' },
          { key: 'current', label: 'Current', align: 'right', render: (r) => fmtMoney(r.current) },
          { key: '1-30', label: '1-30', align: 'right', render: (r) => fmtMoney(r['1-30']) },
          { key: '31-60', label: '31-60', align: 'right', render: (r) => fmtMoney(r['31-60']) },
          { key: '61-90', label: '61-90', align: 'right', render: (r) => fmtMoney(r['61-90']) },
          { key: '90+', label: '90+', align: 'right', render: (r) => <span className="text-rose-300">{fmtMoney(r['90+'])}</span> },
          { key: 'total', label: 'Total', align: 'right', render: (r) => <span className="font-medium">{fmtMoney(r.total)}</span> },
        ]}
        rows={data[who]} empty="No outstanding balances."
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// GST Returns (GSTR-1, GSTR-3B)
// ────────────────────────────────────────────────────────────────────────────
export function GSTReturns() {
  const [tab, setTab] = useState('gstr1');
  const [start, setStart] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(todayISO());
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);

  const refresh = async () => {
    const q = `?start_date=${start}&end_date=${end}`;
    if (tab === 'gstr1') setGstr1(await fb.get(`/gstr-1${q}`));
    else setGstr3b(await fb.get(`/gstr-3b${q}`));
  };
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [tab, start, end]);

  return (
    <div data-testid="fb-gst">
      <PageHeader title="GST Returns" subtitle="GSTR-1 (Outward) · GSTR-3B (Summary)" />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {[{ k: 'gstr1', l: 'GSTR-1' }, { k: 'gstr3b', l: 'GSTR-3B' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${tab === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Input label="From" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          <Input label="To" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      {tab === 'gstr1' && gstr1 && (
        <div data-testid="report-gstr1">
          <div className="grid grid-cols-5 gap-3 mb-3">
            <Kpi label="Taxable" value={fmtMoney(gstr1.totals.taxable)} />
            <Kpi label="CGST" value={fmtMoney(gstr1.totals.cgst)} />
            <Kpi label="SGST" value={fmtMoney(gstr1.totals.sgst)} />
            <Kpi label="IGST" value={fmtMoney(gstr1.totals.igst)} />
            <Kpi label="Total Tax" value={fmtMoney(gstr1.totals.total_tax)} tone="orange" />
          </div>
          <Table
            columns={[
              { key: 'invoice_no', label: 'Invoice #', render: (r) => <span className="font-mono text-xs">{r.invoice_no}</span> },
              { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
              { key: 'customer_name', label: 'Customer' },
              { key: 'customer_gstin', label: 'GSTIN' },
              { key: 'place_of_supply', label: 'POS' },
              { key: 'taxable_value', label: 'Taxable', align: 'right', render: (r) => fmtMoney(r.taxable_value) },
              { key: 'cgst', label: 'CGST', align: 'right', render: (r) => fmtMoney(r.cgst) },
              { key: 'sgst', label: 'SGST', align: 'right', render: (r) => fmtMoney(r.sgst) },
              { key: 'igst', label: 'IGST', align: 'right', render: (r) => fmtMoney(r.igst) },
              { key: 'invoice_value', label: 'Total', align: 'right', render: (r) => <span className="font-medium">{fmtMoney(r.invoice_value)}</span> },
            ]}
            rows={gstr1.rows} empty="No invoices in this period."
          />
        </div>
      )}

      {tab === 'gstr3b' && gstr3b && (
        <div className="grid grid-cols-2 gap-4" data-testid="report-gstr3b">
          <div className={`${PANEL} rounded-xl p-5`}>
            <h3 className="text-white font-medium mb-3">Outward Supplies</h3>
            <Row k="Taxable Value" v={fmtMoney(gstr3b.outward.taxable)} />
            <Row k="CGST" v={fmtMoney(gstr3b.outward.cgst)} />
            <Row k="SGST" v={fmtMoney(gstr3b.outward.sgst)} />
            <Row k="IGST" v={fmtMoney(gstr3b.outward.igst)} />
            <Row k="Total Output Tax" v={fmtMoney(gstr3b.outward.total_tax)} bold />
          </div>
          <div className={`${PANEL} rounded-xl p-5`}>
            <h3 className="text-white font-medium mb-3">Input Tax Credit</h3>
            <Row k="Taxable Value" v={fmtMoney(gstr3b.input.taxable)} />
            <Row k="CGST" v={fmtMoney(gstr3b.input.cgst)} />
            <Row k="SGST" v={fmtMoney(gstr3b.input.sgst)} />
            <Row k="IGST" v={fmtMoney(gstr3b.input.igst)} />
            <Row k="Total Input Tax" v={fmtMoney(gstr3b.input.total_tax)} bold />
          </div>
          <div className={`${SOFT} rounded-xl p-5 col-span-2`}>
            <h3 className="text-white font-medium mb-3">Net GST Payable / (Refund)</h3>
            <div className="grid grid-cols-4 gap-3">
              <Kpi label="CGST" value={fmtMoney(gstr3b.net_payable.cgst)} tone={gstr3b.net_payable.cgst > 0 ? 'red' : 'green'} />
              <Kpi label="SGST" value={fmtMoney(gstr3b.net_payable.sgst)} tone={gstr3b.net_payable.sgst > 0 ? 'red' : 'green'} />
              <Kpi label="IGST" value={fmtMoney(gstr3b.net_payable.igst)} tone={gstr3b.net_payable.igst > 0 ? 'red' : 'green'} />
              <Kpi label="Total" value={fmtMoney(gstr3b.net_payable.total)} tone={gstr3b.net_payable.total > 0 ? 'red' : 'green'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'border-t border-white/10 pt-2 mt-2 text-white font-medium' : 'text-white/70 text-sm'}`}>
      <span>{k}</span><span className="tabular-nums">{v}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Journal Entries (manual)
// ────────────────────────────────────────────────────────────────────────────
export function Journals() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: todayISO(), narration: '', lines: [{ account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 }] });
  const reload = async () => { setRows(await fb.get('/journals')); setAccounts(await fb.get('/accounts')); };
  useEffect(() => { reload(); }, []);

  const drTot = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const crTot = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = Math.abs(drTot - crTot) < 0.01 && drTot > 0;

  const save = async () => {
    try {
      if (!balanced) return toast.error('Journal not balanced');
      if (!form.narration) return toast.error('Narration required');
      const payload = { ...form, lines: form.lines.filter((l) => l.account_id).map((l) => ({ ...l, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) };
      await fb.post('/journals', payload);
      toast.success('Journal posted');
      setOpen(false); setForm({ date: todayISO(), narration: '', lines: [{ account_id: '', debit: 0, credit: 0 }, { account_id: '', debit: 0, credit: 0 }] }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="fb-journals">
      <PageHeader title="Journal Entries" subtitle={`${rows.length} entries · all auto-posted from documents · plus manual`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="fb-new-journal">+ Manual Journal</Btn>} />
      <Table
        columns={[
          { key: 'je_no', label: '#', render: (r) => <span className="font-mono text-xs">{r.je_no}</span> },
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'narration', label: 'Narration' },
          { key: 'entity', label: 'Source', render: (r) => <span className="text-xs text-white/55">{r.entity || 'manual'}</span> },
          { key: 'debit_total', label: 'Amount', align: 'right', render: (r) => <span className="tabular-nums">{fmtMoney(r.debit_total)}</span> },
        ]}
        rows={rows} empty="No journal entries yet."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Manual Journal" width="max-w-3xl">
        <div className="grid grid-cols-3 gap-3">
          <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input label="Reference" value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <Input label="Narration *" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} />
        </div>
        <div className="mt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Lines</div>
          {form.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2">
              <select className="col-span-6 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-white" value={l.account_id}
                onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, account_id: e.target.value }; setForm({ ...form, lines }); }}>
                <option value="">— Account —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
              <input className="col-span-3 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-right tabular-nums text-white" placeholder="Debit" type="number" value={l.debit} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, debit: e.target.value, credit: 0 }; setForm({ ...form, lines }); }} />
              <input className="col-span-3 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-right tabular-nums text-white" placeholder="Credit" type="number" value={l.credit} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, credit: e.target.value, debit: 0 }; setForm({ ...form, lines }); }} />
            </div>
          ))}
          <Btn variant="ghost" onClick={() => setForm({ ...form, lines: [...form.lines, { account_id: '', debit: 0, credit: 0 }] })}>+ Add Line</Btn>
        </div>
        <div className={`mt-4 text-sm flex justify-end gap-6 ${balanced ? 'text-emerald-300' : 'text-rose-300'}`}>
          <span>Debit: <span className="tabular-nums">{fmtMoney(drTot)}</span></span>
          <span>Credit: <span className="tabular-nums">{fmtMoney(crTot)}</span></span>
          <span>{balanced ? '✓ Balanced' : '✗ Not balanced'}</span>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} disabled={!balanced} data-testid="je-save">Post Journal</Btn>
        </div>
      </Drawer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Settings
// ────────────────────────────────────────────────────────────────────────────
export function Settings() {
  const [s, setS] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => setS(await fb.get('/settings')))(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const upd = { ...s };
      delete upd.id; delete upd.created_at;
      const r = await fb.put('/settings', upd);
      setS(r);
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <div data-testid="fb-settings">
      <PageHeader title="Organization" subtitle="GST profile · fiscal year · period lock" />
      <div className={`${PANEL} rounded-xl p-6 max-w-3xl`}>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Company Name" value={s.company_name || ''} onChange={(e) => setS({ ...s, company_name: e.target.value })} />
          <Input label="GSTIN" value={s.gstin || ''} onChange={(e) => setS({ ...s, gstin: e.target.value })} data-testid="settings-gstin" />
          <Input label="PAN" value={s.pan || ''} onChange={(e) => setS({ ...s, pan: e.target.value })} />
          <Input label="State" value={s.state || ''} onChange={(e) => setS({ ...s, state: e.target.value })} />
          <Input label="State Code" value={s.state_code || ''} onChange={(e) => setS({ ...s, state_code: e.target.value })} data-testid="settings-state-code" />
          <Input label="Base Currency" value={s.base_currency || 'INR'} onChange={(e) => setS({ ...s, base_currency: e.target.value })} />
          <Input label="Currency Symbol" value={s.currency_symbol || '₹'} onChange={(e) => setS({ ...s, currency_symbol: e.target.value })} />
          <Select label="Fiscal Year Start Month" value={s.fy_start_month || 4} onChange={(e) => setS({ ...s, fy_start_month: Number(e.target.value) })}>
            {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}</option>)}
          </Select>
        </div>
        <div className="mt-3 max-w-md">
          <Input label="Period Lock Date (entries on/before this are locked)" type="date" value={(s.lock_date || '').slice(0, 10)} onChange={(e) => setS({ ...s, lock_date: e.target.value })} />
        </div>
        <Input label="Address" value={s.address || ''} onChange={(e) => setS({ ...s, address: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="accent" onClick={save} disabled={busy} data-testid="settings-save">{busy ? 'Saving…' : 'Save Settings'}</Btn>
        </div>
      </div>
    </div>
  );
}
